import type { JSX, ReactNode } from 'react';
import type {
  ActivityResource,
  CompanyDetailResource,
  CreateDealDocumentBody,
  DealDocumentCategoryValue,
  DealDocumentResource,
  DiscoveryReportBody,
  LinkCompanyAccountBody,
  LogActivityBody,
  UpdateActivityBody,
  UnlinkedAccountsResource,
  UpdateCompanyBody,
} from '@majuscule/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { internalFetch } from '../../lib/api';
import {
  crmKeys,
  crmSend,
  crmSendEmpty,
  DISCOVERY_FIELDS,
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORY_LABEL,
  errorMessage,
  MAX_PDF_BYTES,
} from '../../lib/crm';
import { MutationError, useCrmRefresh } from './shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { Field, FieldHint } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { Textarea } from '@/components/ui/textarea';

function Shell({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      {open ? children : null}
    </Dialog>
  );
}

function Footer({
  form,
  label,
  busy,
  onClose,
}: {
  form: string;
  label: string;
  busy: boolean;
  onClose: () => void;
}): JSX.Element {
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onClose}>
        Annuler
      </Button>
      <Button type="submit" form={form} size="lg" disabled={busy}>
        {label}
      </Button>
    </DialogFooter>
  );
}

// --- Compte rendu de R1 -------------------------------------------------------

type Report = Record<(typeof DISCOVERY_FIELDS)[number]['key'], string>;

const EMPTY_REPORT: Report = {
  context: '',
  pains: '',
  currentTools: '',
  decisionMaker: '',
  budget: '',
  timeline: '',
};

/**
 * Writes the structured R1 report of a deal. The API has no edit of a logged
 * activity: an edit logs a new pinned report and unpins the previous one.
 */
export function DiscoveryDialog({
  open,
  companyId,
  dealId,
  previous,
  onClose,
}: {
  open: boolean;
  companyId: string;
  dealId: string;
  previous: { id: string; report: Report } | null;
  onClose: () => void;
}): JSX.Element {
  return (
    <Shell open={open} onClose={onClose}>
      <DiscoveryForm companyId={companyId} dealId={dealId} previous={previous} onClose={onClose} />
    </Shell>
  );
}

function DiscoveryForm({
  companyId,
  dealId,
  previous,
  onClose,
}: {
  companyId: string;
  dealId: string;
  previous: { id: string; report: Report } | null;
  onClose: () => void;
}): JSX.Element {
  const refresh = useCrmRefresh();
  const [report, setReport] = useState<Report>(previous?.report ?? EMPTY_REPORT);
  const empty = Object.values(report).every((value) => value.trim() === '');
  const [submitted, setSubmitted] = useState(false);

  const save = useMutation({
    mutationFn: async (): Promise<void> => {
      const trimmed: DiscoveryReportBody = {
        context: report.context.trim(),
        pains: report.pains.trim(),
        currentTools: report.currentTools.trim(),
        decisionMaker: report.decisionMaker.trim(),
        budget: report.budget.trim(),
        timeline: report.timeline.trim(),
      };
      if (previous !== null) {
        const edit: UpdateActivityBody = { report: trimmed, pinned: true };
        await crmSend<ActivityResource>('PATCH', `/activities/${previous.id}`, edit);
        return;
      }
      const body: LogActivityBody = {
        kind: 'discovery',
        companyId,
        dealId,
        report: trimmed,
        pinned: true,
      };
      await crmSend<ActivityResource>('POST', '/activities', body);
    },
    onSuccess: () => {
      refresh();
      onClose();
    },
  });

  return (
    <DialogContent width={640} aria-describedby={undefined}>
      <DialogHead
        title="Compte rendu de R1"
        description="Rendez-vous découverte, épinglé en haut de l’affaire."
      />
      <form
        id="discovery-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (!empty) {
            save.mutate();
          }
        }}
      >
        <DialogBody>
          {DISCOVERY_FIELDS.map((field) => (
            <Field key={field.key} label={field.label}>
              <Textarea
                rows={2}
                maxLength={4000}
                value={report[field.key]}
                onChange={(event) => {
                  setReport({ ...report, [field.key]: event.target.value });
                }}
              />
            </Field>
          ))}
          {submitted && empty ? (
            <MutationError message="Renseigner au moins une rubrique du compte rendu." />
          ) : null}
          {previous !== null ? (
            <FieldHint>
              Le compte rendu modifié est ajouté au journal ; le précédent y reste, désépinglé.
            </FieldHint>
          ) : null}
          <MutationError
            message={save.isError ? errorMessage(save.error, 'Échec de l’enregistrement.') : null}
          />
        </DialogBody>
      </form>
      <Footer form="discovery-form" label="Enregistrer" busy={save.isPending} onClose={onClose} />
    </DialogContent>
  );
}

// --- Note épinglée ------------------------------------------------------------

export function PinnedNoteDialog({
  company,
  onClose,
}: {
  company: CompanyDetailResource | null;
  onClose: () => void;
}): JSX.Element {
  return (
    <Shell open={company !== null} onClose={onClose}>
      {company !== null ? <PinnedNoteForm company={company} onClose={onClose} /> : null}
    </Shell>
  );
}

function PinnedNoteForm({
  company,
  onClose,
}: {
  company: CompanyDetailResource;
  onClose: () => void;
}): JSX.Element {
  const refresh = useCrmRefresh();
  const [note, setNote] = useState(company.pinnedNote ?? '');
  const save = useMutation({
    mutationFn: () => {
      const body: UpdateCompanyBody = { pinnedNote: note.trim() === '' ? null : note.trim() };
      return crmSend<CompanyDetailResource>('PATCH', `/companies/${company.id}`, body);
    },
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  return (
    <DialogContent aria-describedby={undefined}>
      <DialogHead title="Note épinglée" description={company.name} />
      <form
        id="pinned-note-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <DialogBody>
          <Field label="Note" hint="Affichée en haut de la fiche. Vide : la note est retirée.">
            <Textarea
              rows={5}
              maxLength={2000}
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
          </Field>
          <MutationError
            message={save.isError ? errorMessage(save.error, 'Échec de l’enregistrement.') : null}
          />
        </DialogBody>
      </form>
      <Footer form="pinned-note-form" label="Enregistrer" busy={save.isPending} onClose={onClose} />
    </DialogContent>
  );
}

// --- Documents ----------------------------------------------------------------

export function DocumentDialog({
  open,
  dealId,
  onClose,
}: {
  open: boolean;
  dealId: string;
  onClose: () => void;
}): JSX.Element {
  return (
    <Shell open={open} onClose={onClose}>
      <DocumentForm dealId={dealId} onClose={onClose} />
    </Shell>
  );
}

function DocumentForm({ dealId, onClose }: { dealId: string; onClose: () => void }): JSX.Element {
  const refresh = useCrmRefresh();
  const [mode, setMode] = useState<'file' | 'link'>('file');
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState<DealDocumentCategoryValue>('proposal');
  const [submitted, setSubmitted] = useState(false);
  const errors =
    mode === 'link'
      ? {
          label: label.trim() === '' ? 'Indiquer un libellé.' : undefined,
          url: /^https?:\/\/\S+$/.test(url.trim()) ? undefined : 'Lien complet attendu : https://…',
          file: undefined,
        }
      : {
          label: undefined,
          url: undefined,
          file:
            file === null
              ? 'Choisir un PDF.'
              : file.size > MAX_PDF_BYTES
                ? 'PDF trop volumineux : 20 Mo au plus.'
                : undefined,
        };
  const save = useMutation({
    mutationFn: () => {
      if (mode === 'link') {
        const body: CreateDealDocumentBody = { label: label.trim(), url: url.trim(), category };
        return crmSend<DealDocumentResource>('POST', `/deals/${dealId}/documents`, body);
      }
      const form = new FormData();
      if (file !== null) {
        form.append('file', file);
      }
      form.append('category', category);
      if (label.trim() !== '') {
        form.append('label', label.trim());
      }
      return internalFetch<DealDocumentResource>(`/internal/crm/deals/${dealId}/documents/upload`, {
        method: 'POST',
        body: form,
      });
    },
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  const valid = errors.label === undefined && errors.url === undefined && errors.file === undefined;
  return (
    <DialogContent aria-describedby={undefined}>
      <DialogHead
        title="Ajouter un document"
        description="Proposition commerciale, cahier des charges, contrat : déposer le PDF ou coller un lien."
      />
      <form
        id="document-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (valid) {
            save.mutate();
          }
        }}
      >
        <DialogBody>
          <div role="radiogroup" aria-label="Source" className="flex gap-2">
            {(
              [
                ['file', 'Déposer un PDF'],
                ['link', 'Coller un lien'],
              ] as const
            ).map(([value, text]) => (
              <Button
                key={value}
                type="button"
                size="sm"
                variant={mode === value ? 'default' : 'outline'}
                aria-pressed={mode === value}
                onClick={() => {
                  setMode(value);
                  setSubmitted(false);
                }}
              >
                {text}
              </Button>
            ))}
          </div>
          <Field label="Type">
            <NativeSelect
              value={category}
              onChange={(event) => {
                const found = DOCUMENT_CATEGORIES.find((c) => c === event.target.value);
                if (found !== undefined) {
                  setCategory(found);
                }
              }}
            >
              {DOCUMENT_CATEGORIES.map((value) => (
                <option key={value} value={value}>
                  {DOCUMENT_CATEGORY_LABEL[value]}
                </option>
              ))}
            </NativeSelect>
          </Field>
          {mode === 'file' ? (
            <Field label="PDF" required error={submitted ? errors.file : undefined}>
              <Input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null);
                }}
              />
            </Field>
          ) : null}
          <Field
            label="Libellé"
            required={mode === 'link'}
            error={submitted ? errors.label : undefined}
          >
            <Input
              value={label}
              maxLength={200}
              placeholder={mode === 'file' ? 'Nom du fichier par défaut' : 'Proposition commerciale'}
              onChange={(event) => {
                setLabel(event.target.value);
              }}
            />
          </Field>
          {mode === 'link' ? (
            <Field label="Lien" required error={submitted ? errors.url : undefined}>
              <Input
                type="url"
                value={url}
                maxLength={2000}
                placeholder="https://drive.google.com/…"
                onChange={(event) => {
                  setUrl(event.target.value);
                }}
              />
            </Field>
          ) : null}
          <MutationError
            message={save.isError ? errorMessage(save.error, 'Échec de l’ajout.') : null}
          />
        </DialogBody>
      </form>
      <Footer
        form="document-form"
        label={save.isPending ? 'Envoi…' : 'Ajouter'}
        busy={save.isPending}
        onClose={onClose}
      />
    </DialogContent>
  );
}

// --- Lier un compte existant --------------------------------------------------

export function LinkAccountDialog({
  open,
  companyId,
  onClose,
}: {
  open: boolean;
  companyId: string;
  onClose: () => void;
}): JSX.Element {
  return (
    <Shell open={open} onClose={onClose}>
      <LinkAccountForm companyId={companyId} onClose={onClose} />
    </Shell>
  );
}

function LinkAccountForm({
  companyId,
  onClose,
}: {
  companyId: string;
  onClose: () => void;
}): JSX.Element {
  const refresh = useCrmRefresh();
  const unlinked = useQuery({
    queryKey: crmKeys.unlinked,
    queryFn: () => internalFetch<UnlinkedAccountsResource>('/internal/crm/accounts/unlinked'),
  });
  const [clientId, setClientId] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const link = useMutation({
    mutationFn: () => {
      const body: LinkCompanyAccountBody = { clientId };
      return crmSendEmpty('POST', `/companies/${companyId}/accounts`, body);
    },
    onSuccess: () => {
      refresh({ clients: true });
      onClose();
    },
  });
  const accounts = unlinked.data?.accounts ?? [];
  return (
    <DialogContent aria-describedby={undefined}>
      <DialogHead
        title="Lier un compte existant"
        description="Rattacher à l’entreprise un compte facturé créé hors du CRM."
      />
      <form
        id="link-account-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (clientId !== '') {
            link.mutate();
          }
        }}
      >
        <DialogBody>
          <Field
            label="Compte facturé"
            required
            error={submitted && clientId === '' ? 'Choisir un compte.' : undefined}
            hint={
              unlinked.data !== undefined && accounts.length === 0
                ? 'Tous les comptes facturés sont déjà rattachés à une entreprise.'
                : undefined
            }
          >
            <NativeSelect
              value={clientId}
              disabled={accounts.length === 0}
              onChange={(event) => {
                setClientId(event.target.value);
              }}
            >
              <option value="">{unlinked.isLoading ? 'Chargement…' : 'Choisir un compte'}</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name} ({account.legalName})
                </option>
              ))}
            </NativeSelect>
          </Field>
          <MutationError
            message={link.isError ? errorMessage(link.error, 'Échec du rattachement.') : null}
          />
        </DialogBody>
      </form>
      <Footer
        form="link-account-form"
        label="Lier le compte"
        busy={link.isPending}
        onClose={onClose}
      />
    </DialogContent>
  );
}
